import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@/components/ui/command";
import { ClientProvider } from "@/interfaces";
import { cn, errorHandler } from "@/lib/utils";
import { userService } from "@/services/api/user";
import { CommandItem } from "cmdk";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

interface FuelProviderSelectProps {
  value?: number;
  onChange: (value: number) => void;
  userId?: number;
}

const FuelProviderSelect = ({
  value,
  onChange,
  userId,
}: FuelProviderSelectProps) => {
  const [fuelProviders, setFuelProviders] = useState<ClientProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const loadFuelProviders = async () => {
      try {
        setLoading(true);
        const response = await userService.getFuelProviders(userId ?? 1);

        const transformedFuelProviders = response.data.map((provider) => ({
          ...provider,
          fuel_provider_id: provider.id,
        }));

        setFuelProviders(transformedFuelProviders);
      } catch (err) {
        errorHandler(err, "Erro ao carregar fornecedores.");
      } finally {
        setLoading(false);
      }
    };

    loadFuelProviders();
  }, [userId]);

  const filteredFuelProviders = fuelProviders.filter((provider) =>
    provider.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={loading}
          className={cn(
            "justify-between",
            !value && "text-muted-foreground",
            "dark:border-white",
          )}
        >
          {value
            ? fuelProviders.find((provider) => provider.id === value)?.name
            : "Selecione um fornecedor"}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
        >
          <CommandInput
            placeholder="Procure um fornecedor..."
            className="h-9"
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList>
            <CommandEmpty>Nenhum fornecedor encontrado.</CommandEmpty>
            <CommandGroup>
              {filteredFuelProviders.map((provider) => (
                <CommandItem
                  value={provider.name}
                  key={provider.id}
                  onSelect={() => {
                    setOpen(false);
                    onChange(provider.id);
                  }}
                >
                  {provider.name}
                  <Check
                    className={cn(
                      "ml-auto",
                      provider.id === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default FuelProviderSelect;
